import { useUser } from "@modules/auth/hooks/useUser"
import { useCurrentBox } from "@modules/box/useBox"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { DEFAULT_MAXIMUM_AMOUNT } from "@modules/currency/currency"
import { usePromoCodeList } from "@modules/promoCode/hooks/usePromoCodes"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import { BoxPromoCode } from "@modules/promoCode/types/boxPromoCodeTypes"
import {
  PromoCodeFormikType,
  convertFormikToBoxPromoCodeDraft,
  convertFormikToPromoCodeDraft,
  getPromoCodesValidationSchema,
} from "@modules/promoCode/types/promoCodeFormikType"
import { PromoCode } from "@modules/promoCode/types/promoCodeTypes"
import { useToast } from "@modules/ui/components/huToast"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { useMemo, useState } from "react"

export const usePromoCodeForm = (updatedPromoCode?: PromoCode | BoxPromoCode) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const user = useUser()
  const { result: box } = useCurrentBox()
  const { promoCodeService, boxPromoCodeService } = useServicesContext()
  const toast = useToast()
  const { navigateToPromoCodeDetailsView, navigateToPromoCodeListView } = usePromoCodeSidebar()
  const { fetch: refetechPromoCodeList } = usePromoCodeList(false)

  const formik = useFormik<PromoCodeFormikType>({
    initialValues: {
      id: updatedPromoCode?.id,
      title: updatedPromoCode?.title ?? "",
      code: updatedPromoCode?.code ?? "",
      value: updatedPromoCode?.value ?? undefined,
      currency: box?.stripeAccountCurrency ?? user?.stripeAccountCurrency ?? "EUR",
      onlyNewCustomer: updatedPromoCode?.onlyNewCustomer ?? false,
      maxAttendeesLimited: (updatedPromoCode?.maxAttendees ?? 0) !== 0,
      maxAttendees: updatedPromoCode?.maxAttendees ?? undefined,
      noExpirationDate: !updatedPromoCode?.expirationDate,
      expirationDate: updatedPromoCode?.expirationDate ? dayjs(updatedPromoCode.expirationDate).toDate() : undefined,
      noDurationLimit: !updatedPromoCode?.durationInMonths,
      durationInMonths: updatedPromoCode?.durationInMonths ?? undefined,
      type: updatedPromoCode?.type ?? CouponType.PERCENT,
      programs: updatedPromoCode && "programs" in updatedPromoCode ? updatedPromoCode.programs.map((p) => p.id) : [],
      programBundles:
        updatedPromoCode && "programBundles" in updatedPromoCode
          ? updatedPromoCode.programBundles.map((pb) => pb.id)
          : [],
      boxOffers:
        updatedPromoCode && "boxOffers" in updatedPromoCode ? updatedPromoCode.boxOffers.map((bo) => bo.id) : [],
    },
    validationSchema: getPromoCodesValidationSchema(
      !!box,
      user?.stripeMinimumAmount ?? 0,
      user?.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT,
    ),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      if (values.id) {
        try {
          if (!!box) {
            await boxPromoCodeService.updateBoxPromoCode(
              values.id,
              convertFormikToBoxPromoCodeDraft(formik.values as PromoCodeFormikType),
            )
          } else {
            await promoCodeService.updatePromoCode(
              values.id,
              convertFormikToPromoCodeDraft(formik.values as PromoCodeFormikType),
            )
          }

          toast.show({
            severity: "success",
            summary: word("promoCode.form.submit.success"),
          })

          setTimeout(async () => {
            const newPromoCodeList = await refetechPromoCodeList()
            navigateToPromoCodeListView()
            const newUpdatedPromoCode: PromoCode | BoxPromoCode | undefined = newPromoCodeList?.find(
              (p) => p.id === updatedPromoCode?.id,
            )
            if (newUpdatedPromoCode) setTimeout(() => navigateToPromoCodeDetailsView(newUpdatedPromoCode), 50)
          }, 500)
        } catch (e: any) {
          console.error("Error on create objective", e)
          const regex = /An active promotion code with.*already exists\./

          toast.show({
            severity: "error",
            summary: word("global.error.label"),
            detail: regex.test(e.message)
              ? word("promoCode.form.submit.errorDuplicate", { code: formik.values.code })
              : e.message,
          })
        } finally {
          setIsSubmitting(false)
        }
      } else {
        try {
          let newPromoCode: PromoCode | BoxPromoCode
          if (!!box) {
            newPromoCode = await boxPromoCodeService.createBoxPromoCode(
              convertFormikToBoxPromoCodeDraft(formik.values as PromoCodeFormikType),
            )
          } else {
            newPromoCode = await promoCodeService.createPromoCode(
              convertFormikToPromoCodeDraft(formik.values as PromoCodeFormikType),
            )
          }

          toast.show({
            severity: "success",
            summary: word("promoCode.form.submit.success"),
          })

          setTimeout(() => {
            navigateToPromoCodeDetailsView(newPromoCode, true)
          }, 500)
        } catch (e: any) {
          console.error("Error on create objective", e)
          const regex = /An active promotion code with.*already exists\./

          toast.show({
            severity: "error",
            summary: word("global.error.label"),
            detail: regex.test(e.message)
              ? word("promoCode.form.submit.errorDuplicate", { code: formik.values.code })
              : e.message,
          })
        } finally {
          setIsSubmitting(false)
        }
      }
    },
  })

  const promoCodeTypesOptions = useMemo(() => {
    return [
      { value: CouponType.PERCENT, label: word("promoCode.form.input.type.value.percentage") },
      { value: CouponType.DIRECT, label: word("promoCode.form.input.type.value.fixedAmount") },
    ]
  }, [])

  const expirationOptions = useMemo(() => {
    return [
      { value: true, label: word("promoCode.form.input.expiration.value.unlimited") },
      { value: false, label: word("promoCode.form.input.expiration.value.limited") },
    ]
  }, [])

  const applicationDurationOptions = useMemo(() => {
    return [
      { value: true, label: word("promoCode.form.input.applicationDuration.value.unlimited") },
      { value: false, label: word("promoCode.form.input.applicationDuration.value.limited") },
    ]
  }, [])

  const maxAttendeesOptions = useMemo(() => {
    return [
      { value: true, label: word("promoCode.form.input.maxAttendees.value.limited") },
      { value: false, label: word("promoCode.form.input.maxAttendees.value.unlimited") },
    ]
  }, [])

  const onSelectProgram = (id: string) => {
    if (formik.values.programs && formik.values.programs.indexOf(id) >= 0) {
      formik.setFieldValue(
        "programs",
        [...formik.values.programs].filter((programId) => programId !== id),
      )
    } else formik.setFieldValue("programs", [...(formik.values.programs || [])].concat(id))
  }

  const onSelectAllPrograms = (allIds: string[]) => {
    if (allIds.length === formik.values.programs.length) {
      formik.setFieldValue("programs", [])
    } else formik.setFieldValue("programs", allIds)
  }

  const onSelectProgramBundle = (id: string) => {
    if (formik.values.programBundles && formik.values.programBundles.indexOf(id) >= 0) {
      formik.setFieldValue(
        "programBundles",
        [...formik.values.programBundles].filter((programBundleId) => programBundleId !== id),
      )
    } else formik.setFieldValue("programBundles", [...(formik.values.programBundles || [])].concat(id))
  }

  const onSelectAllProgramBundles = (allIds: string[]) => {
    if (allIds.length === formik.values.programBundles.length) {
      formik.setFieldValue("programBundles", [])
    } else formik.setFieldValue("programBundles", allIds)
  }

  const onSelectBoxOffer = (id: string) => {
    if (formik.values.boxOffers && formik.values.boxOffers.indexOf(id) >= 0) {
      formik.setFieldValue(
        "boxOffers",
        [...formik.values.boxOffers].filter((boxOfferId) => boxOfferId !== id),
      )
    } else formik.setFieldValue("boxOffers", [...(formik.values.boxOffers || [])].concat(id))
  }

  const onSelectAllBoxOffers = (allIds: string[]) => {
    if (allIds.length === formik.values.boxOffers.length) {
      formik.setFieldValue("boxOffers", [])
    } else formik.setFieldValue("boxOffers", allIds)
  }

  return {
    formik,
    isSubmitting,
    promoCodeTypesOptions,
    expirationOptions,
    applicationDurationOptions,
    maxAttendeesOptions,
    onSelectProgram,
    onSelectAllPrograms,
    onSelectProgramBundle,
    onSelectAllProgramBundles,
    onSelectBoxOffer,
    onSelectAllBoxOffers,
  }
}
